import React, { useState } from "react";
import { Trash2, ShieldOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Privacy controls card: lets the user wipe ALL their conversation history
 * on demand. Reinforces the "we keep nothing sensitive" promise.
 */
export default function PrivacyControlsCard() {
  const [deleting, setDeleting] = useState(false);

  const handleWipe = async () => {
    const ok = window.confirm(
      "¿Borrar TODAS sus conversaciones?\n\n" +
        "Esto eliminará permanentemente toda la transcripción de las llamadas con la IA. " +
        "No se puede deshacer. Esta acción NO afecta su PIN ni su plan."
    );
    if (!ok) return;
    setDeleting(true);
    try {
      const { data } = await api.delete("/conversations/all");
      toast.success(`${data.deleted} conversación(es) eliminadas. Su historial está limpio.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo borrar el historial");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section
      data-testid="dashboard-privacy-controls"
      className="rounded-2xl border-2 border-zinc-200 bg-white p-5 mb-5"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-zinc-800 flex items-center justify-center">
          <ShieldOff className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest font-black text-zinc-700">
            Privacidad · Control de sus datos
          </p>
          <p className="text-xs text-zinc-500">No guardamos nada sensible. Usted manda.</p>
        </div>
      </div>

      <ul className="text-xs text-zinc-700 space-y-1.5 mb-4 leading-snug">
        <li className="flex gap-2">
          <span className="text-emerald-600 font-black flex-shrink-0">✓</span>
          Nunca pedimos su nombre real, dirección ni Número A.
        </li>
        <li className="flex gap-2">
          <span className="text-emerald-600 font-black flex-shrink-0">✓</span>
          Las transcripciones de sus llamadas se borran automáticamente <span className="font-bold">a los 30 días</span>.
        </li>
        <li className="flex gap-2">
          <span className="text-emerald-600 font-black flex-shrink-0">✓</span>
          Nunca compartimos información con ICE, USCIS ni ninguna autoridad.
        </li>
      </ul>

      <Button
        data-testid="dashboard-wipe-conversations"
        onClick={handleWipe}
        disabled={deleting}
        variant="outline"
        className="w-full h-11 border-2 border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 font-bold rounded-xl"
      >
        {deleting ? (
          <Loader2 className="w-4 h-4 animate-spin mr-2" />
        ) : (
          <Trash2 className="w-4 h-4 mr-2" />
        )}
        Borrar mis conversaciones ahora
      </Button>
    </section>
  );
}
